import React from 'react';
import { Quote } from 'lucide-react';

interface Testimonial {
  quote: string;
  highlight: string;
  name: string;
  company: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "I was worried clients would complain about seeing card fees. They didn't. They just picked bank transfer.",
    highlight: "We process about $70k a month. Before KeepMore, card fees were around $2k/month. Now roughly 80% of payments come in via bank transfer. That's $18k+ per year back.",
    name: "Marcus T.",
    company: "Web + product agency"
  },
  {
    quote: "This isn't a growth tool. It's a leak plug.",
    highlight: "We weren't trying to optimize payments. We just got tired of Stripe quietly skimming $1,500–$2,000 a month. We're saving ~$14k a year on roughly $500k in annual billings.",
    name: "Sarah K.",
    company: "Digital agency"
  },
  {
    quote: "Clients already understand fees. We just never showed them.",
    highlight: "Once we made bank transfer the default, most people chose it without comment. On $40k/month, we went from about $1,200 in fees to under $300.",
    name: "David R.",
    company: "B2B services"
  },
  {
    quote: "It didn't change how we sell. It changed how we get paid.",
    highlight: "Same proposals. Same Stripe account. Same subscriptions. On $30k/month, we're saving around $700–$800 monthly. That's real money over a year.",
    name: "Jessica M.",
    company: "Brand and design agency"
  },
  {
    quote: "I assumed clients would push back. They didn't.",
    highlight: "We bill mostly retainers. About $55k/month. After switching, roughly 75% moved to bank transfer. Our annual savings are tracking at $10k–$12k.",
    name: "Chris L.",
    company: "Marketing agency"
  },
  {
    quote: "This feels obvious in hindsight.",
    highlight: "Agencies already run on thin margins. Losing 3% on every invoice adds up fast. KeepMore just makes the cost visible and gives clients a choice.",
    name: "Amanda B.",
    company: "Productized services agency"
  }
];

const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-[0_2px_6px_rgba(0,0,0,0.05)] flex flex-col h-full">
    <Quote className="w-8 h-8 text-[#0070F3]/20 mb-4 flex-shrink-0" />
    
    <div className="flex-1">
      <blockquote className="text-base font-medium text-[#111827] mb-3 leading-relaxed">
        "{testimonial.quote}"
      </blockquote>
      
      <p className="text-sm text-[#6B7280] leading-relaxed">
        {testimonial.highlight}
      </p>
    </div>
    
    <div className="flex items-center gap-3 pt-4 border-t border-[#E5E7EB] mt-auto flex-shrink-0">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0070F3] to-[#0059CC] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
        {testimonial.name.charAt(0)}
      </div>
      <div>
        <p className="font-semibold text-[#111827] text-sm">{testimonial.name}</p>
        <p className="text-xs text-[#6B7280]">{testimonial.company}</p>
      </div>
    </div>
  </div>
);

const TestimonialsCarousel: React.FC = () => {
  return (
    <section className="w-full py-16 bg-[#F9FAFB] relative z-10 overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl min-w-0">
        <h2 className="text-2xl md:text-3xl font-bold text-[#000000] text-center mb-12">
          Just a few of our many happy users
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto min-w-0">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;